import React, { useCallback, useContext } from 'react';

import { TemplateDirectiveConfig } from '../../../@types/Templates';
import { TemplateEntityContext } from '../../../context/TemplateEntityContext';
import { useTemplateSteps, useTemplateStepsPreview } from '../../../hooks/useTemplateList';
import { Button } from '../../Button';
import Modal from '../../Modal/NewModal';
import { HeaderOption } from '../TemplateEntity';
import { StepHolder } from './StepHolder';

interface CustomFieldModalProps {
  data: TemplateDirectiveConfig[];
  header: string;
  isOpen: boolean;
  toggleCustomFieldModal: () => void;
  onSave: (steps: TemplateDirectiveConfig[], header: string) => void;
  dropDownOptions: HeaderOption[];
  firstDataLine: string[];
}

export function CustomFieldModal({
  data,
  header,
  isOpen,
  toggleCustomFieldModal,
  onSave,
  dropDownOptions,
  firstDataLine
}: CustomFieldModalProps) {
  const [steps, dispatchSteps] = useTemplateSteps(data);

  if (steps.length <= 0) {
    dispatchSteps({ type: 'add' });
  }

  const {
    referenceData: { rows: referenceRows }
  } = useContext(TemplateEntityContext);

  const { data: preview } = useTemplateStepsPreview(firstDataLine, steps, referenceRows);

  const handleSave = useCallback(() => {
    onSave(steps, header);
    toggleCustomFieldModal();
  }, [header, onSave, steps, toggleCustomFieldModal]);

  const LeftModalControls = useCallback(
    () => (
      <div className="flex gap-4">
        <Button onClick={() => dispatchSteps({ type: 'add' })} title="Add Step" />
      </div>
    ),
    [dispatchSteps]
  );

  const RightModalControls = useCallback(
    () => (
      <div className="flex gap-4">
        <Button kind="secondary" onClick={toggleCustomFieldModal} title="Cancel" />
        <Button onClick={handleSave} title="Save" />
      </div>
    ),
    [handleSave, toggleCustomFieldModal]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={toggleCustomFieldModal}
      header="Custom Operation Editor"
      size="large"
      customFooterActions={{
        left: <LeftModalControls />,
        right: <RightModalControls />
      }}
    >
      <div className="flex flex-col w-full px-10 pt-10 overflow-y-auto">
        {steps.map((step, idx) => (
          <StepHolder
            key={`${idx}-${step.operation}-${step.args[0]}`}
            index={idx}
            step={step}
            onUpdate={(data) => {
              dispatchSteps({ type: 'update', index: idx, data });
            }}
            onRemove={() => dispatchSteps({ type: 'remove', index: idx })}
            dropDownOptions={dropDownOptions}
            preview={preview?.steps[idx] || 'No Preview'}
          />
        ))}
      </div>
    </Modal>
  );
}
